import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import { Header } from '../components/Header';
import { ToolCard } from '../components/ToolCard';
import { useFavoritesContext } from '../contexts/FavoritesContext';
import { useTools } from '../hooks/useTools';
import { useAuth } from '../hooks/useAuth';
export function FavoritesPage() {
  const {
    favorites,
    loading: favoritesLoading
  } = useFavoritesContext();
  const {
    tools,
    loading: toolsLoading
  } = useTools();
  const {
    user,
    signInWithGoogle
  } = useAuth();
  const loading = favoritesLoading || toolsLoading;
  // Filter tools to only show favorites
  const favoriteTools = tools.filter(tool => favorites.includes(tool.id));
  return <div className="min-h-screen bg-black" data-id="element-818">
      <Header data-id="element-819" />

      <main className="pt-24 pb-16 px-4" data-id="element-820">
        <div className="max-w-7xl mx-auto" data-id="element-821">
          {/* Page Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-12" data-id="element-822">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-pink-400 text-sm font-medium mb-6" data-id="element-823">
              <Heart size={14} className="fill-pink-400" data-id="element-824" />
              <span data-id="element-825">Your Favorites</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-black text-white mb-4 font-hero" data-id="element-826">
              Saved Tools
            </h1>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-827">
              {user ? <>Your favorite AI tools, synced across all devices</> : <>
                  Your favorite AI tools, saved locally.{' '}
                  <button onClick={signInWithGoogle} className="text-cyan-400 hover:text-cyan-300 underline" data-id="element-828">
                    Sign in to sync
                  </button>
                </>}
            </p>
          </motion.div>

          {/* Loading State */}
          {loading && <div className="text-center py-20" data-id="element-829">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent" data-id="element-830"></div>
              <p className="text-gray-400 mt-4" data-id="element-831">Loading your favorites...</p>
            </div>}

          {/* Empty State */}
          {!loading && favoriteTools.length === 0 && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-center py-20" data-id="element-832">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-833">
                <Heart size={32} className="text-gray-500" data-id="element-834" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3" data-id="element-835">
                No favorites yet
              </h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto" data-id="element-836">
                Start exploring and click the heart icon on any tool to save it
                here
              </p>
              <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors" data-id="element-837">
                <Sparkles size={18} data-id="element-838" />
                Explore Tools
              </a>
            </motion.div>}

          {/* Favorites Grid */}
          {!loading && favoriteTools.length > 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-839">
              {favoriteTools.map((tool, index) => <motion.div key={tool.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} data-id="element-840">
                  <ToolCard tool={tool} data-id="element-841" />
                </motion.div>)}
            </motion.div>}

          {/* Stats */}
          {!loading && favoriteTools.length > 0 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="mt-12 text-center" data-id="element-842">
              <p className="text-sm text-gray-500" data-id="element-843">
                {favoriteTools.length}{' '}
                {favoriteTools.length === 1 ? 'tool' : 'tools'} saved
                {user && ' • Synced to your account'}
              </p>
            </motion.div>}
        </div>
      </main>
    </div>;
}